import React, { useState, useEffect, useRef } from 'react';
import { Button, Typography } from 'src/components/common';
import { InputField } from 'src/components/common/formFields';
import { CompanyAPIService } from 'src/services/company.service';
import { openPopup } from 'src/store/common/common.slice';
import { useDispatch } from 'react-redux';
// import { useGetIdentity } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { useUploadMedia, useGetIdentity } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getCompanyPresignedUrl, getCompany } from 'src/store/company/company.slice';
import { TrashIcon, UploadIcon } from 'src/components/common/common.icons';
import { TGetCompany, TGetCompanyResponse, TGetCompanyPresignedUrlActionRequest } from 'src/services/apiEndpoint.types.ts';
import { RisePattern, RocketIcon, htractionWhite, IdeaBulb } from 'src/assets/images';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './investorCard.module.scss';

interface FormData {
  startupName: string;
  companyEmailAddress: string;
  website: string;
  shortBio: string;
  linkedin: string;
  termsAccepted: boolean;
}

const InvestorCard = (): React.ReactElement => {
  const dispatch = useDispatch();
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getIdentities } = useGetIdentity();
  const { authenticatedId, loggedInUserType, authenticatedEmail } = getIdentities();
  const companyService = new CompanyAPIService();
  const [loading, setLoading] = useState(false);
  const [comp, setComp] = useState<TGetCompany | null>(null);
  const [companyLogoData, setCompanyLogoData] = useState<string | ArrayBuffer | null>(null);
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const selectedImageRef = useRef<HTMLImageElement>(null);
  const dragDropRef = useRef<HTMLDivElement>(null);
  const { handleUploadMedia } = useUploadMedia();
  const { companyProfilePic } = useAppSelector((store) => store.company);

  // Load company data on mount
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await companyService.getCompany<TGetCompanyResponse>({
          id: authenticatedId
        });

        if (response.status && response.data?.company) {
          console.log('Fetched company data:', response.data.company);
          setComp(response.data.company);
          // Fetch company logo data
          await appDispatch(getCompany({ id: response.data.company.id }));
        }
      } catch (error) {
        console.error('Error fetching company:', error);
      }
    };

    if (authenticatedId) {
      fetchCompanyData();
    }
  }, [authenticatedId, appDispatch]);

  const [formData, setFormData] = useState<FormData>({
    startupName: '',
    companyEmailAddress: authenticatedEmail || '',
    website: '',
    shortBio: '',
    linkedin: '',
    termsAccepted: false
  });

  // Update form data when company data changes
  useEffect(() => {
    if (comp) {
      setFormData({
        startupName: comp.name || '',
        companyEmailAddress: comp.companyEmailAddress || authenticatedEmail || '',
        website: comp.website || '',
        shortBio: comp.about || '',
        linkedin: comp.linkedin || '',
        termsAccepted: comp.termsAndConditions || false
      });
    } else {
      setFormData({
        startupName: '',
        companyEmailAddress: authenticatedEmail || '',
        website: '',
        shortBio: '',
        linkedin: '',
        termsAccepted: false
      });
    }
  }, [comp, authenticatedEmail]);

  // Initialize company logo if it exists
  useEffect(() => {
    if (companyProfilePic) {
      setCompanyLogoData(companyProfilePic);
    }
  }, [companyProfilePic]);

  const handleInputChange = (field: keyof FormData) => (value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openImageFileManager = async () => {
    if (uploadImageRef.current) {
      uploadImageRef.current.click();
    }
  };

  const onMediaLoad = (data: string | ArrayBuffer | null) => {
    setCompanyLogoData(data);
  };

  const removeCompanyLogo = () => {
    setCompanyLogoData(null);
  };

  const renderSelectedImage = () => {
    const ele = selectedImageRef.current;
    if (!ele || !companyLogoData) return;

    if (typeof companyLogoData === 'string') {
      ele.src = companyLogoData;
    } else {
      const blob = new Blob([companyLogoData], { type: 'image/jpeg' });
      const imageUrl = URL.createObjectURL(blob);
      ele.src = imageUrl;
    }
  };

  useEffect(() => {
    renderSelectedImage();
  }, [companyLogoData]);

  const handleSubmit = async () => {
    if (!formData.startupName || !formData.companyEmailAddress || !formData.linkedin || !formData.termsAccepted) {
      dispatch(
        openPopup({
          popupMessage: 'Please fill all required fields and accept terms & conditions',
          popupType: 'error'
        })
      );
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.companyEmailAddress)) {
      dispatch(
        openPopup({
          popupMessage: 'Please enter a valid company email address',
          popupType: 'error'
        })
      );
      return;
    }

    try {
      setLoading(true);
      let response;
      let companyId;

      if (comp?.id) {
        companyId = comp.id;
        // If company exists, update it
        response = await companyService.editCompany({
          id: comp.id,
          name: formData.startupName || comp.name,
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          website: formData.website || 'N/A',
          about: formData.shortBio || 'N/A',
          linkedin: formData.linkedin || 'N/A',
          termsAndConditions: formData.termsAccepted,
          introduction: comp.introduction || '',
          foundedYear: comp.foundedYear || 0,
          contactUsUrl: comp.contactUsUrl || '',
          addressLine1: comp.addressLine1 || '',
          addressLine2: comp.addressLine2 || '',
          country: comp.country || '',
          state: comp.state || '',
          city: comp.city || '',
          zipCode: comp.zipCode || '',
          industry: comp.industry || '',
          facebook: comp.facebook || '',
          twitter: comp.twitter || '',
          instagram: comp.instagram || '',
          contactNumber: comp.contactNumber || 0 // Keep existing contact number
        });
      } else {
        // If company doesn't exist, create it
        response = await companyService.createCompany({
          email: authenticatedEmail,
          name: formData.startupName,
          type: 'Start up',
          companyEmailAddress: formData.companyEmailAddress,
          website: formData.website || 'N/A',
          about: formData.shortBio || 'N/A',
          linkedin: formData.linkedin,
          termsAndConditions: formData.termsAccepted,
          consentTimeStamp: new Date().toISOString(),
          navigateTo: '/score'
        });
        companyId = response.data?.company?.id;
      }

      if (response.status) {
        // Upload logo if provided
        if (companyLogoData && companyId) {
          try {
            const uploadProfilePicturePayload: TGetCompanyPresignedUrlActionRequest = {
              id: companyId as string,
              data: companyLogoData,
              showPopup: false
            };
            await appDispatch(getCompanyPresignedUrl(uploadProfilePicturePayload));
            
            // Refresh company data to ensure we have the latest state including the new logo
            await appDispatch(getCompany({ id: companyId }));
          } catch (logoError) {
            console.error('Error uploading logo:', logoError);
            dispatch(
              openPopup({
                popupMessage: 'Company details saved but there was an error uploading the logo.',
                popupType: 'warning'
              })
            );
          }
        }

        if (formData.termsAccepted) {
          navigate('/score');
        }
      } else {
        dispatch(
          openPopup({
            popupMessage: response.message || 'Failed to save company',
            popupType: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        openPopup({
          popupMessage: 'An error occurred while creating company',
          popupType: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.leftSection}>
        <div ><img src={htractionWhite} alt="htraction" className={styles.logo}/></div>
        <div className={styles.content}>
          <div className={styles.card}>
            <img src={IdeaBulb} alt="Idea Illustration" className={styles.cardIllustration}/>
            <h1 className={styles.title}>Discover Startups that<br />Truly Match Your Thesis</h1>
            <p className={styles.subtitle}>No endless pitches. No noise.</p>
          </div>
          <img src={RisePattern} alt="Rise Pattern" className={styles.bgPattern} />
        </div>
      </div>
      
      <div className={styles.rightSection}>
        <div className={styles.formContainer}>
          <div className={styles.formHeader}>
            <div className={styles.rocketIcon}>
              <img src={RocketIcon} alt="htraction" />
            </div>
            <h2 className={styles.formTitle}>Setup Your Account</h2>
          </div>
          <div className={styles.formContent}>
            <div className={styles.formField}>
              <InputField
                id="startup-name"
                label="Entity Name"
                value={formData.startupName}
                onChange={(e) => handleInputChange('startupName')(e.target.value)}
                placeholder="xyz.pvt.ltd"
                required
              />
            </div>
            <div className={styles.formField}>
              <InputField
                id="company-email"
                label="Contact Email"
                value={formData.companyEmailAddress}
                onChange={(e) => handleInputChange('companyEmailAddress')(e.target.value)}
                placeholder="Enter contact email address"
                type="email"
                required
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <InputField
                  id="linkedin"
                  label="LinkedIn"
                  value={formData.linkedin}
                  onChange={(e) => handleInputChange('linkedin')(e.target.value)}
                  placeholder="https://linkedin.com/company/"
                  type="url"
                  required
                />
              </div>
              <div className={styles.formField}>
                <InputField
                  id="website"
                  label="Website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website')(e.target.value)}
                  placeholder="https://"
                  type="url"
                />
              </div>
            </div>
            <div className={styles.formField}>
              <InputField
                id="short-bio"
                label="About"
                value={formData.shortBio}
                onChange={(e) => handleInputChange('shortBio')(e.target.value)}
                placeholder="Briefly tell me about your entity"
                type="text"
              />
            </div>
            <div className={styles.uploadSection}>
              <Typography as="p" variant="caption">
                Upload your company logo
              </Typography>
              <div
                className={styles.uploadLogoContainer}
                draggable
                onClick={openImageFileManager}
                ref={dragDropRef}
                onDrop={(e) => {
                  e.preventDefault();
                  handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image', dragDrop: true });
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                }}
              >
                <span className={styles.dragDropArea}>
                  <UploadIcon />
                  <Typography as="p" variant="body-2" fontWeight="regular">
                    Click to upload or drag and drop
                  </Typography>
                  <input
                    type="file"
                    hidden
                    ref={uploadImageRef}
                    accept=".bmp,.jpg,.jpeg,.png"
                    className={styles.selectPostImage}
                    onChange={(e) => {
                      handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'image' });
                    }}
                  />
                </span>
              </div>
              {companyLogoData && (
                <div className={styles.selectedImage}>
                  <div
                    className={styles.deleteButton}
                    onClick={removeCompanyLogo}
                  >
                    <TrashIcon />
                  </div>
                  <img alt="uploaded logo" ref={selectedImageRef} />
                </div>
              )}
            </div>
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="terms"
                checked={formData.termsAccepted}
                onChange={(e) => handleInputChange('termsAccepted')(e.target.checked)}
              />
              <label htmlFor="terms" className={styles.termsText}>
                I accept the <a href="/termsandconditions" className={styles.termsLink}>terms & conditions</a>
              </label>
            </div>
            <Button 
              variant="primary" 
              onClick={handleSubmit} 
              disabled={loading}
              className={styles.submitButton}
              style={{ position: 'relative' }}
            >
              <span style={{ opacity: loading ? 0 : 1 }}>
                Save & Continue
              </span>
              {loading && (
                <div 
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #fff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
              )}
            </Button>
            <style>
              {`
                @keyframes spin {
                  0% { transform: translate(-50%, -50%) rotate(0deg); }
                  100% { transform: translate(-50%, -50%) rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorCard;
